<script setup>
import { usePlatformApi } from '@/utils/platformApi'

// Sama persis pola auth-check halaman /platform lain — lihat komentar di
// platform/tenants/index.vue untuk penjelasan lengkap dua lapis keamanannya.
definePage({ meta: { layout: 'platform', public: true } })

const router = useRouter()

const platformAccessToken = useCookie('platformAccessToken')
const platformAdminData = useCookie('platformAdminData')

if (!platformAccessToken.value)
  await router.push('/platform/login')

// ─── State ────────────────────────────────────────────────────────────────
// 2026-08-07 (follow-up Addendum 05): monitoring langganan yang akan segera
// berakhir -- read-only murni, buat admin follow-up manual (telepon/WA).
// Perpanjangan sendiri sudah self-service (Modul L), jadi TIDAK ADA aksi
// apa pun di halaman ini, cuma daftar kontak.
const subscriptions = ref([])
const loading = ref(false)
const search = ref('')
const page = ref(1)
const perPage = ref(20)
const totalItems = ref(0)

const headers = [
  { title: 'Tenant', key: 'tenant', sortable: false },
  { title: 'Paket', key: 'plan', sortable: false },
  { title: 'Berakhir', key: 'expires_at', sortable: false },
  { title: 'Sisa Hari', key: 'days_remaining', sortable: false, align: 'center' },
  { title: 'Kontak', key: 'contact', sortable: false },
]

const formatDate = iso => (iso ? new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) : '-')

const daysRemainingColor = days => {
  if (days <= 1) return 'error'
  if (days <= 3) return 'warning'

  return 'default'
}

const handleAuthError = async err => {
  const status = err?.status ?? err?.response?.status
  if (status === 401 || status === 403) {
    platformAccessToken.value = null
    platformAdminData.value = null
    await router.push('/platform/login')

    return true
  }

  return false
}

const fetchSubscriptions = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (search.value) query.search = search.value

    const res = await usePlatformApi('/api/v1/platform/subscriptions/expiring-soon', { query }).get()
    const body = res?.data ?? res

    subscriptions.value = body?.data ?? []
    totalItems.value = body?.pagination?.total ?? 0
  } catch (err) {
    if (await handleAuthError(err)) return

    console.error('[Platform] fetch expiring subscriptions error', err)
    subscriptions.value = []
  } finally {
    loading.value = false
  }
}

watch([page, search], fetchSubscriptions, { immediate: true })
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap gap-3">
      <div>
        <h4 class="text-h4">
          Langganan Akan Berakhir
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Login sebagai {{ platformAdminData?.name }}
        </p>
      </div>
    </div>

    <VCard>
      <VCardText class="text-body-2 text-medium-emphasis">
        Tenant dengan langganan berakhir dalam 3 hari ke depan. Perpanjangan sudah self-service
        (tenant bayar sendiri lewat Settings &gt; Langganan) — daftar ini murni untuk follow-up
        manual (telepon/WA) kalau belum ada tanda-tanda mereka akan bayar.
      </VCardText>

      <VDivider />

      <VCardText class="d-flex align-center">
        <VTextField
          v-model="search"
          label="Cari nama tenant"
          density="compact"
          style="max-width: 320px;"
          clearable
        />
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:page="page"
        :headers="headers"
        :items="subscriptions"
        :items-length="totalItems"
        :loading="loading"
        :items-per-page="perPage"
        no-data-text="Tidak ada langganan yang akan berakhir dalam 3 hari ke depan."
      >
        <template #item.tenant="{ item }">
          <div v-if="item.tenant">
            <div class="font-weight-medium">
              {{ item.tenant.name }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ item.tenant.slug }}
            </div>
          </div>
          <span v-else class="text-medium-emphasis">-</span>
        </template>

        <template #item.plan="{ item }">
          {{ item.plan?.name ?? '-' }}
        </template>

        <template #item.expires_at="{ item }">
          {{ formatDate(item.expires_at) }}
        </template>

        <template #item.days_remaining="{ item }">
          <VChip
            :color="daysRemainingColor(item.days_remaining)"
            size="small"
          >
            {{ item.days_remaining }} hari
          </VChip>
        </template>

        <template #item.contact="{ item }">
          <div v-if="item.tenant">
            <div class="text-caption">
              {{ item.tenant.email }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ item.tenant.phone ?? '-' }}
            </div>
          </div>
          <span v-else class="text-medium-emphasis">-</span>
        </template>
      </VDataTableServer>
    </VCard>
  </div>
</template>
